import cn from 'classnames';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { FormattedDate } from 'react-intl';

import { IconButton, PrimarySolidButton, SecondaryGhostButton, TextButton } from '@bibliocommons/base-buttons';
import { Dropdown, DropdownMenuItem } from '@bibliocommons/base-dropdown';
import { ChevronDown, Edit } from '@bibliocommons/base-icons';
import { SecondaryGhostLink } from '@bibliocommons/base-links';
import TextField from '@bibliocommons/text-field';

import { Modal, ModalContent, ModalFooter, ModalHeader } from 'app/components/shared/Modal';
import Heading from 'app/components/shared/Heading';
import ReOrderButtons from 'app/components/admin/browse/shared/ReOrderButtons';
import Section from 'app/components/admin/browse/shared/Section';
import { ADMIN_BROWSE_SECTION_TYPE, BROWSE_HOME_PAGE_STATUS } from 'app/constants/admin/browse/home';
import { makeEmptySection, validateBrowseHomePage } from 'app/helpers/admin/browse/home';
import { browseHomePageShape } from 'app/shapes/admin/browse/home';
import SectionEditorCatalogTitles from './SectionEditorCatalogTitles';
import SectionEditorLinks from './SectionEditorLinks';
import SectionEditorLists from './SectionEditorLists';
import SectionEditorListsHero from './SectionEditorListsHero';
import SectionEditorListTitles from './SectionEditorListTitles';
import SectionEditorNavigation from './SectionEditorNavigation';

import './AdminBrowseHomeEditForm.scss';

const DEFAULT_PAGE_NAME = 'Untitled home page';

const DROPDOWN_LABEL_MAP = {
  [ADMIN_BROWSE_SECTION_TYPE.CATALOG_TITLES]: 'Catalog title',
  [ADMIN_BROWSE_SECTION_TYPE.LINKS]: 'Link',
  [ADMIN_BROWSE_SECTION_TYPE.LISTS]: 'List card',
  [ADMIN_BROWSE_SECTION_TYPE.LISTS_HERO]: 'List hero',
  [ADMIN_BROWSE_SECTION_TYPE.LIST_TITLES]: 'List title',
  [ADMIN_BROWSE_SECTION_TYPE.NAVIGATION]: 'Navigation'
};

const AdminBrowseHomeEditForm = ({ browseHomePage, hasUnsavedChanges, isSaving, onChange, onSave, previewPageUrl }) => {
  const [state, setState] = useState({
    isSectionExpandedIndex: browseHomePage.sections.reduce((acc, section) => {
      acc[section.sectionId] = false;
      return acc;
    }, {}),
    validation: null
  });

  const { isSectionExpandedIndex, validation } = state;

  const { name, sections, status, updatedAt, updatedBy } = browseHomePage || {};

  const [pageNameState, setPageNameState] = useState({
    isEditing: false,
    value: browseHomePage?.name
  });

  const handleAddSection = sectionType => {
    const emptySection = makeEmptySection(sectionType);

    setState(prevState => {
      return {
        ...prevState,
        isSectionExpandedIndex: {
          ...prevState.isSectionExpandedIndex,
          [emptySection.sectionId]: true
        }
      };
    });

    const newBrowseHomePage = {
      ...browseHomePage,
      sections: [...browseHomePage.sections, emptySection]
    };

    onChange(newBrowseHomePage);
  };

  const handleSectionDelete = sectionToDelete => {
    const sectionIndex = browseHomePage.sections.findIndex(section => section.sectionId === sectionToDelete.sectionId);
    const newSections = [...browseHomePage.sections];

    newSections.splice(sectionIndex, 1);

    const newBrowseHomePage = {
      ...browseHomePage,
      sections: newSections
    };

    onChange(newBrowseHomePage);

    setState(prevState => {
      const newIsSectionExpandedIndex = { ...prevState.isSectionExpandedIndex };

      delete newIsSectionExpandedIndex[sectionToDelete.sectionId];

      return {
        ...prevState,
        isSectionExpandedIndex: newIsSectionExpandedIndex
      };
    });
  };

  const handleSubmit = event => {
    event.preventDefault();

    const newValidation = validateBrowseHomePage(browseHomePage);
    const { isValid, validationBySectionId } = newValidation;

    setState(prevState => {
      const { isSectionExpandedIndex: prevIsSectionExpandedIndex } = prevState;
      const newIsSectionExpandedIndex = Object.keys(validationBySectionId).reduce((acc, sectionId) => {
        acc[sectionId] = prevIsSectionExpandedIndex[sectionId] || !validationBySectionId[sectionId].isValid;
        return acc;
      }, {});

      return {
        ...prevState,
        isSectionExpandedIndex: newIsSectionExpandedIndex,
        validation: newValidation
      };
    });

    if (isValid) {
      onSave();
    }
  };

  const handleSectionChange = newSection => {
    const sectionIndex = browseHomePage.sections.findIndex(section => section.sectionId === newSection.sectionId);
    const newSections = [...browseHomePage.sections];

    newSections[sectionIndex] = newSection;

    const newBrowseHomePage = {
      ...browseHomePage,
      sections: newSections
    };

    onChange(newBrowseHomePage);
  };

  const handleSectionsReOrder = newSections => {
    const newBrowseHomePage = {
      ...browseHomePage,
      sections: newSections
    };

    onChange(newBrowseHomePage);
  };

  const handleSectionIsExpandedToggle = sectionId => {
    setState(prevState => {
      return {
        ...prevState,
        isSectionExpandedIndex: {
          ...prevState.isSectionExpandedIndex,
          [sectionId]: !prevState.isSectionExpandedIndex[sectionId]
        }
      };
    });
  };

  const { isEditing: isEditingPageName, value: pageNameValue } = pageNameState;

  const openEditPageNameModal = () => {
    setPageNameState(prevState => {
      return {
        ...prevState,
        isEditing: true
      };
    });
  };

  const closeEditPageNameModal = () => {
    setPageNameState(prevState => {
      return {
        ...prevState,
        isEditing: false
      };
    });
  };

  const handleEditPageNameChange = newPageName => {
    setPageNameState(prevState => {
      return {
        ...prevState,
        value: newPageName
      };
    });
  };

  const handleEditPageNameSave = () => {
    setPageNameState(prevState => {
      return {
        ...prevState,
        isEditing: false
      };
    });

    const newBrowseHomePage = {
      ...browseHomePage,
      name: pageNameValue || DEFAULT_PAGE_NAME
    };

    onChange(newBrowseHomePage);
  };

  const handleEditPageNameSubmit = event => {
    event.preventDefault();

    handleEditPageNameSave();
  };

  return (
    <div className="cp-admin-browse-home-edit-form">
      <Modal handleClose={closeEditPageNameModal} key="BrowseAdminHomeEdit_EditName" open={isEditingPageName}>
        <ModalHeader title="Edit page title" />
        <ModalContent>
          <form onSubmit={handleEditPageNameSubmit}>
            <TextField
              hideLabel
              id="BrowseAdminHomeEdit_PageNameInput"
              label="Page title"
              onChange={handleEditPageNameChange}
              type="text"
              value={pageNameValue}
            />
          </form>
        </ModalContent>
        <ModalFooter handlePrimaryAction={handleEditPageNameSave} primaryActionButtonText="Save title" />
      </Modal>

      <form onSubmit={handleSubmit}>
        <div className="row">
          <div className="col col-12 col-xl-4">
            <Heading size="large" tagName="h1" variant>
              {name}&nbsp;
              <IconButton
                className="button-edit"
                dataKey="AdminBrowseEdit_OpenEditPageNameModal"
                handleClick={openEditPageNameModal}
                type="button"
              >
                <Edit />
              </IconButton>
            </Heading>
          </div>
          <div className="col col-12 col-xl-8">
            <div className="save-wrapper">
              <p className="meta">
                {status !== BROWSE_HOME_PAGE_STATUS.UNKNOWN && (
                  <span className="meta-item">
                    <span className="meta-label">Status</span>
                    <span>{status === BROWSE_HOME_PAGE_STATUS.ACTIVE ? 'Active' : 'Draft'}</span>
                  </span>
                )}
                {updatedBy && (
                  <span className="meta-item">
                    <span className="meta-label">Last updated</span>
                    <span>
                      {updatedBy} on <FormattedDate day="numeric" month="long" value={updatedAt} year="numeric" />
                    </span>
                  </span>
                )}
              </p>

              <SecondaryGhostLink
                className={cn('action-preview', { 'action-preview--disabled': hasUnsavedChanges })}
                dataKey="AdminBrowseHomeEditPreviewLink"
                href={previewPageUrl}
                isLoading={isSaving}
                target="_blank"
                type="button"
              >
                Preview
              </SecondaryGhostLink>
              <PrimarySolidButton
                className="action-save"
                dataKey="AdminBrowseHomeEditSaveButton"
                disabled={!hasUnsavedChanges}
                isLoading={isSaving}
                type="submit"
              >
                Save
              </PrimarySolidButton>
            </div>
          </div>
        </div>

        <div className="sections">
          {sections.map((section, sectionIndex) => {
            const { sectionId, sectionType } = section;
            const isValidSectionType = Object.values(ADMIN_BROWSE_SECTION_TYPE).includes(sectionType);
            if (!isValidSectionType) {
              return null;
            }

            const heading = DROPDOWN_LABEL_MAP[sectionType];
            const isExpanded = isSectionExpandedIndex[sectionId];
            const sectionValidation = validation?.validationBySectionId[sectionId];

            const renderReOrderButtons = () => {
              return (
                <ReOrderButtons index={sectionIndex} items={browseHomePage.sections} onChange={handleSectionsReOrder} />
              );
            };

            const SectionComponent = (() => {
              switch (sectionType) {
                case ADMIN_BROWSE_SECTION_TYPE.CATALOG_TITLES:
                  return SectionEditorCatalogTitles;
                case ADMIN_BROWSE_SECTION_TYPE.LINKS:
                  return SectionEditorLinks;
                case ADMIN_BROWSE_SECTION_TYPE.LISTS:
                  return SectionEditorLists;
                case ADMIN_BROWSE_SECTION_TYPE.LISTS_HERO:
                  return SectionEditorListsHero;
                case ADMIN_BROWSE_SECTION_TYPE.LIST_TITLES:
                  return SectionEditorListTitles;
                case ADMIN_BROWSE_SECTION_TYPE.NAVIGATION:
                  return SectionEditorNavigation;
                default:
                  throw new Error(`Unkown sectionType: ${sectionType}`);
              }
            })();

            return (
              <Section
                className="section"
                confirmDeleteModalMessage={`Are you sure you want to delete the '${heading}' row on the '${name}' page?`}
                key={sectionId}
                heading={heading}
                isExpanded={isExpanded}
                onDelete={() => handleSectionDelete(section)}
                onIsExpandedToggle={handleSectionIsExpandedToggle}
                renderReOrderButtons={renderReOrderButtons}
                sectionId={sectionId}
              >
                <div className="section-body">
                  <SectionComponent section={section} onChange={handleSectionChange} validation={sectionValidation} />
                </div>
              </Section>
            );
          })}
        </div>
      </form>

      <Dropdown
        renderTrigger={handleToggle => (
          <SecondaryGhostButton dataKey="AddNewSectionToggle" handleClick={handleToggle} type="button">
            Add new row <ChevronDown className="icon-chevron-down" />
          </SecondaryGhostButton>
        )}
        dataKey="AddNewSectionDropdown"
      >
        {Object.values(ADMIN_BROWSE_SECTION_TYPE).map(adminBrowseSectionType => {
          return (
            <DropdownMenuItem key={`${adminBrowseSectionType}_DropdownMenuItem`}>
              <TextButton
                block
                dataKey={`${adminBrowseSectionType}_ItemButton`}
                handleClick={() => handleAddSection(adminBrowseSectionType)}
                type="button"
              >
                {DROPDOWN_LABEL_MAP[adminBrowseSectionType]}
              </TextButton>
            </DropdownMenuItem>
          );
        })}
      </Dropdown>
    </div>
  );
};

AdminBrowseHomeEditForm.propTypes = {
  browseHomePage: browseHomePageShape.isRequired,
  hasUnsavedChanges: PropTypes.bool.isRequired,
  isSaving: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  previewPageUrl: PropTypes.string.isRequired
};

export default AdminBrowseHomeEditForm;
